import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { useTenantContext } from '@/shared/providers/TenantProvider';
import { useAuth } from '@/shared/providers/AuthProvider';
import { createRuntimeConfigService } from '@/shared/services/runtimeConfigService';
import type { RuntimeConfig } from '@/shared/types/runtimeConfig.types';
import { setServerTimezone } from '@/shared/utils/serverTimeContext';

/**
 * Hook to fetch and access runtime configuration parameters
 *
 * This hook fetches ~200 configuration parameters from the API that control
 * feature toggles, business rules, and system settings.
 *
 * @returns Runtime configuration data, loading state, and helper functions
 */
export const useRuntimeConfig = () => {
  const apiClient = useApiClient();
  const { tenantId } = useTenantContext();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Stabilize the service object — previously called on every render which
  // caused getConfigValue / isFeatureEnabled to be new function references
  // on every render, breaking downstream useMemo/useEffect dependency arrays
  // and causing the configuration API to be called in a continuous loop.
  const configService = useMemo(
    () => createRuntimeConfigService(apiClient),
    [apiClient],
  );

  const {
    data: config,
    isLoading,
    error,
    refetch,
  } = useQuery<RuntimeConfig>({
    queryKey: ['runtime-config', tenantId],
    queryFn: () => configService.fetchConfiguration(),
    // useQuery already handles the initial fetch when isAuthenticated becomes true.
    // No extra invalidateQueries call is needed on mount.
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });

  // Clear cached config on logout so the next login always fetches fresh data.
  // useQuery's `enabled: isAuthenticated` handles re-fetching when the user
  // logs back in — no manual invalidation needed for login.
  useEffect(() => {
    if (!isAuthenticated) {
      queryClient.removeQueries({ queryKey: ['runtime-config', tenantId] });
    }
  }, [isAuthenticated, tenantId, queryClient]);

  useEffect(() => {
    if (config) {
      const timezone = config['general.timezone'];
      if (timezone) {
        setServerTimezone(String(timezone));
      }
    }
  }, [config]);

  /**
   * Gets a configuration value by key with optional default.
   * Wrapped in useCallback so the function reference is stable across renders —
   * consumers that list this as a useMemo/useEffect dependency do not re-run
   * unnecessarily (previously caused infinite re-render / refetch loops).
   */
  const getConfigValue = useCallback(<T = string | number | boolean>(
    key: string,
    defaultValue?: T
  ): T | undefined => {
    return configService.getConfigValue<T>(config || null, key, defaultValue);
  }, [configService, config]);

  /**
   * Checks if a feature is enabled.
   * Wrapped in useCallback for the same stability reasons as getConfigValue.
   */
  const isFeatureEnabled = useCallback((featureKey: string): boolean => {
    return configService.isFeatureEnabled(config || null, featureKey);
  }, [configService, config]);

  return {
    config,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch,
    getConfigValue,
    isFeatureEnabled,
  };
};
